import React, {useCallback, useMemo, useRef, useState} from 'react'
import {Box, Typography} from '@mui/material'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import styles from './styles'

export interface FileDropZoneProps {
  fileTypeLabel: string
  onFilesSelected: (files: FileList) => void
  accept?: string | string[]
  disabled?: boolean
  helperText?: string
  icon?: React.ReactNode
  onDragStateChange?: (isActive: boolean) => void
  inputAriaLabel?: string
}

const FileDropZone: React.FC<FileDropZoneProps> = ({
  fileTypeLabel,
  onFilesSelected,
  accept,
  disabled = false,
  helperText,
  icon,
  onDragStateChange,
  inputAriaLabel,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [isDragActive, setIsDragActive] = useState(false)

  const normalizedAccept = useMemo(() => {
    if (!accept) {
      return undefined
    }
    return Array.isArray(accept) ? accept.join(',') : accept
  }, [accept])

  const trimmedLabel = useMemo(() => fileTypeLabel.trim() || 'file', [fileTypeLabel])
  const lowerCaseLabel = useMemo(() => trimmedLabel.toLowerCase(), [trimmedLabel])
  const effectiveHelperText = useMemo(
    () => helperText ?? `or click to choose a ${lowerCaseLabel} file from your computer`,
    [helperText, lowerCaseLabel],
  )
  const effectiveInputAriaLabel = useMemo(
    () => inputAriaLabel ?? `Select ${lowerCaseLabel} files`,
    [inputAriaLabel, lowerCaseLabel],
  )

  const updateDragState = useCallback(
    (nextState: boolean) => {
      setIsDragActive(prevState => {
        if (prevState === nextState) {
          return prevState
        }
        onDragStateChange?.(nextState)
        return nextState
      })
    },
    [onDragStateChange],
  )

  const handleBrowseClick = useCallback(() => {
    if (disabled) {
      return
    }
    fileInputRef.current?.click()
  }, [disabled])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (disabled) {
        return
      }
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        handleBrowseClick()
      }
    },
    [disabled, handleBrowseClick],
  )

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (disabled) {
        event.target.value = ''
        return
      }
      const {files} = event.target
      if (!files || files.length === 0) {
        event.target.value = ''
        return
      }
      onFilesSelected(files)
      event.target.value = ''
    },
    [disabled, onFilesSelected],
  )

  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault()
      event.stopPropagation()
      if (disabled) {
        return
      }
      updateDragState(true)
    },
    [disabled, updateDragState],
  )

  const handleDragLeave = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault()
      event.stopPropagation()
      updateDragState(false)
    },
    [updateDragState],
  )

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault()
      event.stopPropagation()
      updateDragState(false)
      if (disabled) {
        return
      }
      const {files} = event.dataTransfer
      if (files && files.length > 0) {
        onFilesSelected(files)
      }
    },
    [disabled, onFilesSelected, updateDragState],
  )

  const iconNode = icon ?? <CloudUploadOutlinedIcon color="primary" sx={styles.icon} />
  return (
    <>
      <Box
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        aria-label={`Drag and drop your ${trimmedLabel} files`}
        sx={[styles.dropZone, isDragActive ? styles.dropZoneActive : {}]}
        onDragOver={handleDragOver}
        onDragEnter={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleBrowseClick}
        onKeyDown={handleKeyDown}>
        {iconNode}
        <Typography variant="subtitle1">Drag &amp; drop your {trimmedLabel} file</Typography>
        <Typography variant="body2" sx={styles.helper}>
          {effectiveHelperText}
        </Typography>
      </Box>

      <input
        ref={fileInputRef}
        type="file"
        accept={normalizedAccept}
        hidden
        onChange={handleInputChange}
        aria-label={effectiveInputAriaLabel}
        disabled={disabled}
      />
    </>
  )
}

export default FileDropZone
